
import React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandInput, 
  CommandItem,
  CommandList 
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toTitleCase } from '@/utils/toTitleCase';

interface CategoryFilterProps {
  categories: string[];
  selectedCategories: string[];
  onSelectCategories: (categories: string[]) => void;
  isLoading?: boolean;
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  selectedCategories,
  onSelectCategories,
  isLoading = false,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectCategories([]);
  };

  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      onSelectCategories(selectedCategories.filter(c => c !== category));
    } else {
      onSelectCategories([...selectedCategories, category]);
    }
  };

  const safeCategories = Array.isArray(categories) ? categories : [];

  const displayLabel = selectedCategories.length === 0
    ? (isLoading ? "Loading..." : "Category")
    : selectedCategories.length === 1
      ? toTitleCase(selectedCategories[0])
      : `${selectedCategories.length} categories`;

  const isActive = selectedCategories.length > 0;

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "justify-between rounded-full h-8 px-3 text-xs border transition-all",
              isActive
                ? "bg-primary/20 border-primary/40 text-foreground hover:bg-primary/30"
                : "bg-white/5 border-white/10 hover:bg-white/10 text-muted-foreground"
            )}
            disabled={isLoading}
          >
            <span className="truncate">{displayLabel}</span>
            {isActive ? (
              <span
                role="button"
                aria-label="Clear selected categories"
                className="ml-1.5 p-0.5 rounded-full hover:bg-white/20 transition-colors"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectCategories([]);
                  setOpen(false);
                }}
              >
                <X className="h-3 w-3 shrink-0" />
              </span>
            ) : (
              <ChevronDown className="ml-1.5 h-3 w-3 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0">
          <Command>
            <CommandInput placeholder="Search category..." />
            <CommandList>
              <CommandEmpty>No category found.</CommandEmpty>
              <CommandGroup>
                {safeCategories.map((category) => (
                  <CommandItem
                    key={category}
                    value={category}
                    onSelect={() => toggleCategory(category)}
                  >
                    <Checkbox
                      checked={selectedCategories.includes(category)}
                      className="mr-2 h-4 w-4 pointer-events-none"
                    />
                    {toTitleCase(category)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CategoryFilter;
